class FieldDefinitionsUtilities {
  static #_FIELD_TYPES = new Set(['string', 'number', 'boolean', 'date', 'enum', 'objectId']);
  static #_MATCH_MODES = new Set(['exact', 'contains']);
  static #_SUGGESTION_SOURCES = new Set(['static', 'query', 'none']);
  static #_OPERATORS = new Set(['and', 'or']);

  static fromJsonSchema(jsonSchema, overlay = {}) {
    if (!FieldDefinitionsUtilities.#_isPlainObject(jsonSchema)
      || jsonSchema.type !== 'object'
      || !FieldDefinitionsUtilities.#_isPlainObject(jsonSchema.properties)) {
      throw new Error('SCHEMA_NOT_DERIVABLE');
    }
    const fields = {};
    FieldDefinitionsUtilities.#_deriveProperties(jsonSchema.properties, '', fields);
    const overlayFields = overlay.fields;
    if (FieldDefinitionsUtilities.#_isPlainObject(overlayFields)) {
      for (const [path, declaration] of Object.entries(overlayFields)) {
        fields[path] = { ...fields[path], ...declaration };
      }
    }
    const result = { fields };
    if (overlay.defaultOperator !== undefined) {
      result.defaultOperator = overlay.defaultOperator;
    }
    if (overlay.searchableFields !== undefined) {
      result.searchableFields = [...overlay.searchableFields];
    }
    FieldDefinitionsUtilities.assertValid(result);
    return result;
  }

  static assertValid(fieldDefinitions) {
    if (!FieldDefinitionsUtilities.#_isPlainObject(fieldDefinitions)) {
      throw new Error('FIELD_DEFINITIONS_INVALID: not a plain object');
    }
    const fields = fieldDefinitions.fields;
    if (!FieldDefinitionsUtilities.#_isPlainObject(fields) || Object.keys(fields).length === 0) {
      throw new Error('FIELD_DEFINITIONS_INVALID: fields must declare at least one field');
    }
    for (const [path, declaration] of Object.entries(fields)) {
      FieldDefinitionsUtilities.#_assertFieldValid(path, declaration);
    }
    const operator = fieldDefinitions.defaultOperator;
    if (operator !== undefined && !FieldDefinitionsUtilities.#_OPERATORS.has(operator)) {
      throw new Error(`FIELD_DEFINITIONS_INVALID: defaultOperator '${operator}' is not 'and' or 'or'`);
    }
    const searchable = fieldDefinitions.searchableFields;
    if (searchable !== undefined) {
      if (!Array.isArray(searchable) || searchable.some((entry) => typeof entry !== 'string')) {
        throw new Error('FIELD_DEFINITIONS_INVALID: searchableFields must be an array of strings');
      }
    }
  }

  static resolveSuggestionSource(declaration) {
    if (declaration.suggestions !== undefined) {
      return declaration.suggestions;
    }
    if (declaration.type === 'enum') {
      return 'static';
    }
    return 'query';
  }

  static #_assertFieldValid(path, declaration) {
    if (!FieldDefinitionsUtilities.#_isPlainObject(declaration)) {
      throw new Error(`FIELD_DEFINITIONS_INVALID: field '${path}' is not a plain object`);
    }
    if (!FieldDefinitionsUtilities.#_FIELD_TYPES.has(declaration.type)) {
      throw new Error(`FIELD_DEFINITIONS_INVALID: field '${path}' has unknown type '${declaration.type}'`);
    }
    if (declaration.type === 'enum') {
      const values = declaration.values;
      const valid = Array.isArray(values)
        && values.length > 0
        && values.every((value) => typeof value === 'string' || typeof value === 'number');
      if (!valid) {
        throw new Error(`FIELD_DEFINITIONS_INVALID: enum field '${path}' must declare non-empty values`);
      }
    }
    if (declaration.match !== undefined && !FieldDefinitionsUtilities.#_MATCH_MODES.has(declaration.match)) {
      throw new Error(`FIELD_DEFINITIONS_INVALID: field '${path}' has unknown match '${declaration.match}'`);
    }
    if (declaration.suggestions !== undefined
      && !FieldDefinitionsUtilities.#_SUGGESTION_SOURCES.has(declaration.suggestions)) {
      throw new Error(`FIELD_DEFINITIONS_INVALID: field '${path}' has unknown suggestions '${declaration.suggestions}'`);
    }
  }

  static #_deriveProperties(properties, prefix, fields) {
    for (const [key, property] of Object.entries(properties)) {
      if (!FieldDefinitionsUtilities.#_isPlainObject(property)) {
        continue;
      }
      const path = `${prefix}${key}`;
      if (Array.isArray(property.enum) && property.enum.length > 0) {
        fields[path] = { type: 'enum', values: [...property.enum] };
        continue;
      }
      if (property.type === 'object' && FieldDefinitionsUtilities.#_isPlainObject(property.properties)) {
        FieldDefinitionsUtilities.#_deriveProperties(property.properties, `${path}.`, fields);
        continue;
      }
      if (property.type === 'array' && FieldDefinitionsUtilities.#_isPlainObject(property.items)) {
        FieldDefinitionsUtilities.#_deriveProperties({ [key]: property.items }, prefix, fields);
        continue;
      }
      const derived = FieldDefinitionsUtilities.#_deriveScalarType(property);
      if (derived !== null) {
        fields[path] = { type: derived };
      }
    }
  }

  static #_deriveScalarType(property) {
    if (property.type === 'string') {
      if (property.format === 'date' || property.format === 'date-time') {
        return 'date';
      }
      return 'string';
    }
    if (property.type === 'number' || property.type === 'integer') {
      return 'number';
    }
    if (property.type === 'boolean') {
      return 'boolean';
    }
    return null;
  }

  static #_isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}

export { FieldDefinitionsUtilities };
export default FieldDefinitionsUtilities;
